import { useCallback, useMemo } from 'react'
import { Dropdown, Row, Col } from 'antd'
import { MoreOutlined, CaretDownOutlined, CaretUpOutlined, CheckOutlined } from '@ant-design/icons'

import {
  saveSorting,
  setActiveDropdown,
  removeNegateColumn,
  saveFreeWidthColumn,
  saveNegateColumns,
  saveAllFreeWidthColumn,
  resetFreeWidthColumns,
  saveNewLineColumns,
  updateSubRuleEmptyColumn,
  setEmptyColumnFromMenu,
} from '../../Slices/Omnichannel/SubRuleSlice'
import { ArticleColumn, TableSortingAscend, TableSortingDescend } from '../../Constants/TableConfiguration'
import { AllFreeWidthColumn } from '../../Constants/Configuration'
import FilterIcon from '../Svg/FilterIcon'
import {
  getHtmlParentForDropdown,
  renderTranslation,
  retrieveRenderNewLineColumn,
  stopPropagation,
} from '../../Helpers/GenericHelpers'
import { TranslationKeys } from '../../Constants/TranslationKeys'
import { retrieveActiveEmptyColumns } from '../../Helpers/FilterHelper'
import { populateSubRuleTableWithArticles } from '../../Services/Omnichannel/SeasonRuleService'
import { useAppDispatch, useAppSelector } from '../../hooks'

function SubRuleAdditionalMenu(props) {
  const dispatch = useAppDispatch()
  const negateTableColumns = useAppSelector(state => state.present.subRule.negateColumns)
  const sorting = useAppSelector(state => state.present.subRule.sorting)
  const freeWidthColumns = useAppSelector(state => state.present.subRule.freeWidthColumns)
  const activeDropdown = useAppSelector(state => state.present.subRule.activeDropdown)
  const language = useAppSelector(state => state.present.language.value)
  const newLineColumns = useAppSelector(state => state.present.subRule.newLineColumns)
  const emptyTableColumns = useAppSelector(state => state.present.subRule.emptyFilterColumns)
  const defaultCols = useAppSelector(state => state.present.subRule.defaultColumns)

  const negated = useMemo(() => {
    return negateTableColumns.indexOf(props.label) !== -1
  }, [negateTableColumns, props.label])
  const emptyColumns = useMemo(() => {
    return retrieveActiveEmptyColumns(emptyTableColumns, [])
  }, [emptyTableColumns])
  const empty = useMemo(() => {
    return emptyColumns.find(element => element === props.label)
  }, [emptyColumns, props.label])

  const dropdownVisible = useMemo(() => {
    return activeDropdown === props.label ? true : false
  }, [activeDropdown, props.label])

  const hideDropdown = useCallback(() => {
    dispatch(setActiveDropdown(''))
  }, [dispatch])

  const searchArticlesProcedure = useCallback(() => {
    dispatch(populateSubRuleTableWithArticles(false))
  }, [dispatch])

  const switchToDefaultFiltering = useCallback(() => {
    if (negated) {
      dispatch(removeNegateColumn(props.label))
      searchArticlesProcedure()
    }

    if (empty) {
      dispatch(updateSubRuleEmptyColumn({ value: props.label, active: false }))
      searchArticlesProcedure()
    }

    hideDropdown()
  }, [dispatch, negated, empty, hideDropdown, props.label, searchArticlesProcedure])

  const updateNegatedColumnSearch = useCallback(() => {
    if (empty) {
      dispatch(updateSubRuleEmptyColumn({ value: props.label, active: false }))
    }

    dispatch(saveNegateColumns(props.label))
    searchArticlesProcedure()
  }, [dispatch, searchArticlesProcedure, empty, props.label])

  const addToEmptyColumn = useCallback(() => {
    dispatch(setEmptyColumnFromMenu(props.label))
    searchArticlesProcedure()
  }, [dispatch, searchArticlesProcedure, props.label])

  const updateFreeWidthColumn = useCallback(() => {
    dispatch(saveFreeWidthColumn(props.label))
  }, [dispatch, props.label])

  const updateAllFreeWidthColumns = useCallback(() => {
    dispatch(saveAllFreeWidthColumn())
  }, [dispatch])

  const resetAllFreeWidthColumns = useCallback(() => {
    dispatch(resetFreeWidthColumns())
  }, [dispatch])

  const updateNewLine = useCallback(() => {
    dispatch(saveNewLineColumns(props.label))
  }, [dispatch, props.label])

  const openDropdown = useCallback(
    e => {
      e.preventDefault()
      stopPropagation(e)
      if (props.disableDots) return

      dispatch(setActiveDropdown(props.label))
    },
    [props.disableDots, dispatch, props.label],
  )

  const setAscendSorting = useCallback(
    e => {
      dispatch(
        saveSorting({
          sortField: props.label,
          sortOrder: TableSortingAscend,
          pageSize: sorting.pageSize,
          current: sorting.current,
        }),
      )
      searchArticlesProcedure()
    },
    [searchArticlesProcedure, dispatch, props.label, sorting],
  )

  const setDescendSorting = useCallback(
    e => {
      dispatch(
        saveSorting({
          sortField: props.label,
          sortOrder: TableSortingDescend,
          pageSize: sorting.pageSize,
          current: sorting.current,
        }),
      )
      searchArticlesProcedure()
    },
    [searchArticlesProcedure, dispatch, props.label, sorting],
  )

  const resetSortOrder = useCallback(
    e => {
      dispatch(
        saveSorting({
          sortField: '',
          sortOrder: '',
          pageSize: sorting.pageSize,
          current: sorting.current,
        }),
      )
      searchArticlesProcedure()
    },
    [dispatch, searchArticlesProcedure, sorting],
  )

  const setDropdownWindowListener = useCallback(() => {
    if (dropdownVisible) {
      window.addEventListener('click', hideDropdown)
    } else {
      window.removeEventListener('click', hideDropdown)
    }
    return () => {
      window.removeEventListener('click', hideDropdown)
    }
  }, [dropdownVisible, hideDropdown])

  setDropdownWindowListener()

  const downSorting = useMemo(() => {
    return sorting.sortField === props.label && sorting.sortOrder === TableSortingDescend
  }, [props.label, sorting.sortField, sorting.sortOrder])
  const upSorting = useMemo(() => {
    return sorting.sortField === props.label && sorting.sortOrder === TableSortingAscend
  }, [props.label, sorting.sortField, sorting.sortOrder])

  const isArticleCodeColumn = useMemo(() => {
    return props.label === ArticleColumn.ArticleCode
  }, [props.label])
  const renderNewLine = useMemo(() => {
    return retrieveRenderNewLineColumn(props.label, defaultCols)
  }, [defaultCols, props.label])

  const menu = useMemo(() => {
    return {
      className: 'additional-menu',
      onClick: ({ key, keyPath, domEvent }) => {
        stopPropagation(domEvent)
      },
      items: [
        {
          key: 'order',
          icon: <CheckOutlined className='v-hidden' />,
          className: 'bordered',
          label: (
            <span>
              {' '}
              {renderTranslation(language, TranslationKeys.tableDotsMenuSort)}
              {upSorting ? (
                <CaretUpOutlined className='custom-sort-up active float-right' onClick={resetSortOrder} />
              ) : (
                <CaretUpOutlined className={'custom-sort-up float-right'} onClick={setAscendSorting} />
              )}
              {downSorting ? (
                <CaretDownOutlined className='custom-sort-down active float-right' onClick={resetSortOrder} />
              ) : (
                <CaretDownOutlined className='custom-sort-down float-right' onClick={setDescendSorting} />
              )}
            </span>
          ),
        },
        {
          key: 'filter-submenu',
          label: renderTranslation(language, TranslationKeys.tableDotsMenuSetFilter),
          icon: <FilterIcon className='additional-menu-icon filter-icon' />,
          className: 'bordered exclude-modal-click',
          children: [
            {
              key: 'submenu-default',
              icon: <CheckOutlined className={!negated && !empty ? '' : 'v-hidden'} />,
              onClick: switchToDefaultFiltering,
              label: renderTranslation(language, TranslationKeys.tableDotsMenuDefault),
              className: 'exclude-modal-click',
            },
            {
              key: 'submenu-exclusion',
              icon: <CheckOutlined className={negated ? '' : 'v-hidden'} />,
              onClick: updateNegatedColumnSearch,
              label: renderTranslation(language, TranslationKeys.tableDotsMenuExceptFor),
              className: 'exclude-modal-click',
            },
            isArticleCodeColumn
              ? null
              : {
                  key: 'submenu-empty',
                  icon: <CheckOutlined className={empty ? '' : 'v-hidden'} />,
                  onClick: addToEmptyColumn,
                  label: renderTranslation(language, TranslationKeys.tableDotsMenuEmpty),
                  className: 'exclude-modal-click',
                },
          ],
        },
        {
          key: 'adapt-single-column',
          icon: <CheckOutlined className={freeWidthColumns.indexOf(props.label) !== -1 ? '' : 'v-hidden'} />,
          onClick: updateFreeWidthColumn,
          label: renderTranslation(language, TranslationKeys.tableDotsMenuAdaptColumn),
        },
        {
          key: 'adapt-all-column',
          icon: <CheckOutlined className={freeWidthColumns.indexOf(AllFreeWidthColumn) !== -1 ? '' : 'v-hidden'} />,
          onClick: updateAllFreeWidthColumns,
          label: renderTranslation(language, TranslationKeys.tableDotsMenuAdaptAllColumns),
        },
        {
          key: 'reset-column',
          icon: <CheckOutlined className='v-hidden' />,
          onClick: resetAllFreeWidthColumns,
          className: 'bordered',
          label: renderTranslation(language, TranslationKeys.tableDotsMenuResetFixedColumns),
        },
        renderNewLine
          ? {
              key: 'new-line',
              icon: <CheckOutlined className={newLineColumns.indexOf(props.label) !== -1 ? '' : 'v-hidden'} />,
              onClick: updateNewLine,
              className: 'bordered',
              label: renderTranslation(language, TranslationKeys.tableDotsNewLine),
            }
          : null,
      ],
    }
  }, [
    addToEmptyColumn,
    downSorting,
    empty,
    freeWidthColumns,
    isArticleCodeColumn,
    language,
    negated,
    newLineColumns,
    props.label,
    renderNewLine,
    resetAllFreeWidthColumns,
    resetSortOrder,
    setAscendSorting,
    setDescendSorting,
    switchToDefaultFiltering,
    upSorting,
    updateAllFreeWidthColumns,
    updateFreeWidthColumn,
    updateNegatedColumnSearch,
    updateNewLine,
  ])

  return (
    <Dropdown
      className='additional-menu-dropdown'
      menu={menu}
      open={dropdownVisible}
      getPopupContainer={getHtmlParentForDropdown}
    >
      <Row className='adjust-table-head'>
        <Col flex='auto' className='additional-menu-content'>
          {props.children}
        </Col>
        <Col flex='10px'></Col>
        <MoreOutlined
          className={'dots-' + props.label + ' dots-icon article-menu-dots'}
          style={{ top: 'calc(50% - 5px)', transform: 'translateY(-25%)', right: 2, position: 'absolute' }}
          onClick={openDropdown}
        />
      </Row>
    </Dropdown>
  )
}

export default SubRuleAdditionalMenu
